import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import {
  getMyTasks, acceptTask, declineTask, requestTaskReturn, getClaimableCases, setClaimPin, selfClaimCase,
  getCaseComments, addCaseComment, requestDeadlineExtension, submitWork, getDocumentDownload,
} from '../api/admin';

// Assignment Workflow brief — every admin's own task inbox. A task sent to
// you shows a restricted preview (case id, type, country, priority,
// deadlines, message) until you accept it — no images, location, payment
// info or documents before that, per the brief's explicit restriction.
const CASE_TYPE_LABELS = { damage_case: 'Damage claim', application: 'Provider application', support_ticket: 'Support ticket', verification: 'Identity verification' };
const DECLINE_REASONS = [
  { value: 'not_available', label: 'Not available / outside working hours' },
  { value: 'workload_too_high', label: 'Workload too high' },
  { value: 'functional_support_required', label: 'Functional support required' },
  { value: 'wrong_assignment', label: 'Wrong assignment / case is not in my area' },
  { value: 'conflict_of_interest', label: 'Conflict of interest' },
  { value: 'other', label: 'Other reason' },
];

const MyTasks = () => {
  // Named `tr`, not `t` — this file uses `t` as the loop variable for
  // "task" throughout (pending.map((t) => ...), etc.), which would shadow
  // a same-named translate function.
  const { t: tr } = useLanguage();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [declineModal, setDeclineModal] = useState(null); // { assignmentId, reason, note }
  const [returnModal, setReturnModal] = useState(null); // { assignmentId, reason, note }
  const [doneModal, setDoneModal] = useState(null); // { assignmentId, report, busy }
  const [extendModal, setExtendModal] = useState(null); // { assignmentId, minutes, reason, busy }
  const [commentsPanel, setCommentsPanel] = useState(null); // { caseType, caseId, displayId, comments, draft, loading }

  const load = () => { setLoading(true); getMyTasks().then((res) => setTasks(res.data?.tasks || [])).finally(() => setLoading(false)); };
  const [claim, setClaim] = useState({ allowed: false, cases: [], pinSet: false, lockedUntil: null, loaded: false });
  const [claimModal, setClaimModal] = useState(null); // { caseType, caseId, title, pin, error, busy }
  const [newPin, setNewPin] = useState({ pin: '', error: '' });

  const loadClaim = () => getClaimableCases()
    .then((res) => setClaim({ allowed: true, cases: res.data?.cases || [], pinSet: !!res.data?.pin_set, lockedUntil: res.data?.locked_until || null, loaded: true }))
    .catch((err) => setClaim((c) => ({ ...c, allowed: err.response?.status !== 403 ? c.allowed : false, loaded: true })));

  const { permissions } = useAuth();
  const canSelfClaim = !!permissions?.self_claim_cases;

  // Only ask for claimable cases when the account is enabled for it, so
  // non-enabled accounts never send a request the server will refuse.
  useEffect(() => {
    load();
    if (canSelfClaim) loadClaim();
  }, [canSelfClaim]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePin = async () => {
    if (!/^\d{4}$/.test(newPin.pin)) return setNewPin((p) => ({ ...p, error: 'Enter exactly 4 digits.' }));
    try { await setClaimPin(newPin.pin); setNewPin({ pin: '', error: '' }); loadClaim(); }
    catch (err) { setNewPin((p) => ({ ...p, error: err.response?.data?.error || 'Could not save PIN.' })); }
  };

  const submitClaim = async () => {
    if (!claimModal || claimModal.busy) return;
    setClaimModal((m) => ({ ...m, busy: true, error: null }));
    try {
      await selfClaimCase(claimModal.caseType, claimModal.caseId, claimModal.pin);
      setClaimModal(null);
      load(); loadClaim();
    } catch (err) {
      const msg = err.response?.data?.error || 'Claim failed.';
      setClaimModal((m) => ({ ...m, busy: false, error: msg, pin: '' }));
      loadClaim();
    }
  };

  // Any failed action (stale tab, double click, status already changed) shows
  // the server's reason and immediately reloads the list, so counters and
  // buttons always reflect the latest server state.
  const runAction = async (action, onSuccess) => {
    try {
      await action();
      if (onSuccess) onSuccess();
    } catch (err) {
      alert(err.response?.data?.error || 'That action could not be completed.');
    } finally {
      load();
    }
  };

  const [busyIds, setBusyIds] = useState(new Set());
  const doAccept = (assignmentId) => {
    if (busyIds.has(assignmentId)) return;
    setBusyIds((prev) => new Set(prev).add(assignmentId));
    return runAction(() => acceptTask(assignmentId)).finally(() =>
      setBusyIds((prev) => { const n = new Set(prev); n.delete(assignmentId); return n; })
    );
  };

  const submitDecline = () => {
    if (!declineModal.reason || declineModal.busy) return;
    setDeclineModal((m) => ({ ...m, busy: true }));
    return runAction(
      () => declineTask(declineModal.assignmentId, declineModal.reason, declineModal.note),
      () => setDeclineModal(null)
    );
  };

  const submitReturn = () => {
    if (!returnModal.reason || returnModal.busy) return;
    setReturnModal((m) => ({ ...m, busy: true }));
    return runAction(
      () => requestTaskReturn(returnModal.assignmentId, returnModal.reason, returnModal.note),
      () => setReturnModal(null)
    );
  };

  // PDF documentation spec, step 4 — "Submit completed work" requires a
  // completion report. This is NOT acceptance: the Owner decides separately.
  const submitDone = () => {
    if (!doneModal.report.trim() || doneModal.busy) return;
    setDoneModal((m) => ({ ...m, busy: true }));
    return runAction(
      () => submitWork(doneModal.assignmentId, doneModal.report),
      () => setDoneModal(null)
    );
  };

  const downloadDocument = async (documentId) => {
    try {
      const res = await getDocumentDownload(documentId);
      window.open(res.data.url, '_blank');
    } catch (err) {
      alert(err.response?.data?.error || 'Document is not ready yet.');
    }
  };

  // "Staff requests extra time with a reason" -- the old deadline still
  // applies until the owner decides, so this just records the request.
  const submitExtend = () => {
    if (!extendModal.minutes || !extendModal.reason.trim() || extendModal.busy) return;
    setExtendModal((m) => ({ ...m, busy: true }));
    return runAction(
      () => requestDeadlineExtension(extendModal.assignmentId, extendModal.minutes, extendModal.reason),
      () => setExtendModal(null)
    );
  };

  const openComments = (t) => {
    setCommentsPanel({ caseType: t.case_type, caseId: t.case_id, displayId: t.display_id, comments: [], draft: '', loading: true });
    getCaseComments(t.case_type, t.case_id).then((res) => setCommentsPanel((p) => (p ? { ...p, comments: res.data?.comments || [], loading: false } : p)));
  };
  const submitComment = async () => {
    if (!commentsPanel.draft.trim()) return;
    const res = await addCaseComment(commentsPanel.caseType, commentsPanel.caseId, commentsPanel.draft);
    setCommentsPanel((p) => ({ ...p, comments: [...p.comments, res.data.comment], draft: '' }));
  };

  const pending = tasks.filter((t) => t.assignment_status === 'pending_acceptance');
  const active = tasks.filter((t) => t.assignment_status === 'accepted');
  const returning = tasks.filter((t) => t.assignment_status === 'reassignment_requested');
  const submitted = tasks.filter((t) => t.assignment_status === 'submitted_for_acceptance');

  return (
    <div style={s.page}>
      <h1 style={s.h1}>{tr('My Tasks')}</h1>
      <p style={s.hint}>{tr('Cases assigned to you. New tasks need your acceptance or decline before you\'re the responsible handler.')}</p>

      {loading ? <p>Loading…</p> : (
        <>
          {claim.allowed && (
        <>
          <h2 style={s.h2}>{tr('Claim an open case')}</h2>
          {!claim.pinSet ? (
            <div style={s.card}>
              <p style={s.hint}>Set your personal 4-digit claim PIN. It is only used to confirm a claim; it is not your login PIN.</p>
              <div style={s.actions}>
                <input style={s.pinInput} type="password" inputMode="numeric" maxLength={4} placeholder="4 digits" value={newPin.pin} onChange={(e) => setNewPin({ pin: e.target.value.replace(/\D/g, ''), error: '' })} />
                <button style={s.btnGreen} onClick={savePin}>Save PIN</button>
              </div>
              {newPin.error && <div style={s.errorText}>{newPin.error}</div>}
            </div>
          ) : claim.lockedUntil && new Date(claim.lockedUntil) > new Date() ? (
            <div style={s.card}><p style={s.errorText}>Claiming is locked until {new Date(claim.lockedUntil).toLocaleTimeString()} after wrong PIN attempts.</p></div>
          ) : claim.cases.length === 0 ? (
            <p style={s.empty}>No open cases you can claim right now.</p>
          ) : claim.cases.map((c) => (
            <div key={c.case_type + c.case_id} style={s.card}>
              <div style={s.cardTop}>
                <strong>{c.display_id}</strong> <span style={s.muted}>{CASE_TYPE_LABELS[c.case_type]} · {c.country_code || 'No country'}</span>
                <span style={s.priorityBadge}>{(c.priority || 'normal').toUpperCase()}</span>
              </div>
              <div style={s.actions}>
                <button style={s.btnGreen} onClick={() => setClaimModal({ caseType: c.case_type, caseId: c.case_id, title: c.display_id, pin: '', error: null, busy: false })}>{tr('Claim')}</button>
              </div>
            </div>
          ))}
        </>
      )}

      <h2 style={s.h2}>{tr('Awaiting your response')} ({pending.length})</h2>
          {pending.map((t) => (
            <div key={t.assignment_id} style={s.card}>
              <div style={s.cardTop}>
                <strong>{t.display_id}</strong> <span style={s.muted}>{CASE_TYPE_LABELS[t.case_type]} · {t.country_code || 'No country'}</span>
                <span style={s.priorityBadge}>{t.priority?.toUpperCase()}</span>
              </div>
              {t.assignment_message && <p style={s.message}>"{t.assignment_message}"</p>}
              <div style={s.deadlines}>
                <span>Accept by: {t.acceptance_due_at ? new Date(t.acceptance_due_at).toLocaleString() : '—'}</span>
                {t.processing_due_at && <span>Complete by: {new Date(t.processing_due_at).toLocaleString()}</span>}
              </div>
              <div style={s.actions}>
                <button style={{ ...s.btnGreen, opacity: busyIds.has(t.assignment_id) ? 0.6 : 1 }} disabled={busyIds.has(t.assignment_id)} onClick={() => doAccept(t.assignment_id)}>{busyIds.has(t.assignment_id) ? 'Accepting…' : tr('Accept task')}</button>
                <button style={s.btnRed} onClick={() => setDeclineModal({ assignmentId: t.assignment_id, reason: '', note: '' })}>{tr('Decline task')}</button>
              </div>
            </div>
          ))}
          {pending.length === 0 && <p style={s.empty}>{tr('Nothing awaiting your response.')}</p>}

          <h2 style={s.h2}>{tr('In progress')} ({active.length})</h2>
          {active.map((t) => (
            <div key={t.assignment_id} style={s.card}>
              <div style={s.cardTop}>
                <strong>{t.display_id}</strong> <span style={s.muted}>{CASE_TYPE_LABELS[t.case_type]} · {t.country_code || 'No country'}</span>
                <span style={s.priorityBadge}>{t.priority?.toUpperCase()}</span>
              </div>
              {t.processing_due_at && <div style={s.deadlines}><span>Complete by: {new Date(t.processing_due_at).toLocaleString()}</span></div>}
              {t.owner_decision === 'rework_required' && (
                <div style={s.reworkNote}>Rework was requested on your last submission.{t.decision_remarks ? ` Remarks: ${t.decision_remarks}` : ''}</div>
              )}
              <div style={s.actions}>
                <button style={s.btnGreen} onClick={() => setDoneModal({ assignmentId: t.assignment_id, report: '', busy: false })}>{tr('Submit completed work')}</button>
                <button style={s.btnGray} onClick={() => setExtendModal({ assignmentId: t.assignment_id, minutes: 60, reason: '', busy: false })}>{tr('Request more time')}</button>
                <button style={s.btnGray} onClick={() => openComments(t)}>{tr('Comments')}</button>
                <button style={s.btnGray} onClick={() => setReturnModal({ assignmentId: t.assignment_id, reason: '', note: '' })}>{tr('Request return')}</button>
                {t.order_document_id && <button style={s.btnGray} onClick={() => downloadDocument(t.order_document_id)}>{tr('Order Document')} · PDF</button>}
              </div>
            </div>
          ))}
          {active.length === 0 && <p style={s.empty}>{tr('No active tasks.')}</p>}

          {submitted.length > 0 && (
            <>
              <h2 style={s.h2}>{tr('Submitted — awaiting decision')} ({submitted.length})</h2>
              {submitted.map((t) => (
                <div key={t.assignment_id} style={s.card}>
                  <div style={s.cardTop}>
                    <strong>{t.display_id}</strong> <span style={s.muted}>{CASE_TYPE_LABELS[t.case_type]} · submitted {t.submitted_at ? new Date(t.submitted_at).toLocaleString() : ''}</span>
                  </div>
                  <span style={s.muted}>Waiting for the owner's decision — a Delivery Note will be generated once reviewed.</span>
                </div>
              ))}
            </>
          )}

          {returning.length > 0 && (
            <>
              <h2 style={s.h2}>{tr('Return requested')} ({returning.length})</h2>
              {returning.map((t) => (
                <div key={t.assignment_id} style={s.card}>
                  <strong>{t.display_id}</strong> <span style={s.muted}>Waiting for the owner's decision.</span>
                </div>
              ))}
            </>
          )}
        </>
      )}

      {claimModal && (
        <div style={s.modalBackdrop} onClick={() => setClaimModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Claim {claimModal.title}</h3>
            <p style={s.hint}>Enter your 4-digit claim PIN to take this case. It becomes yours immediately.</p>
            <input style={s.pinInput} type="password" inputMode="numeric" maxLength={4} autoFocus placeholder="PIN" value={claimModal.pin} onChange={(e) => setClaimModal((m) => ({ ...m, pin: e.target.value.replace(/\D/g, '') }))} />
            {claimModal.error && <div style={s.errorText}>{claimModal.error}</div>}
            <button style={s.btnGreen} disabled={claimModal.pin.length !== 4 || claimModal.busy} onClick={submitClaim}>{claimModal.busy ? 'Checking…' : 'Confirm claim'}</button>
            <button style={s.btnGray} onClick={() => setClaimModal(null)}>Cancel</button>
          </div>
        </div>
      )}

      {declineModal && (
        <div style={s.modalBackdrop} onClick={() => setDeclineModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Decline task</h3>
            {DECLINE_REASONS.map((r) => (
              <label key={r.value} style={s.radioLabel}>
                <input type="radio" name="decline_reason" checked={declineModal.reason === r.value} onChange={() => setDeclineModal((m) => ({ ...m, reason: r.value }))} /> {r.label}
              </label>
            ))}
            <textarea style={s.textarea} placeholder="Explanation" value={declineModal.note} onChange={(e) => setDeclineModal((m) => ({ ...m, note: e.target.value }))} />
            <button style={s.btnRed} disabled={!declineModal.reason} onClick={submitDecline}>Send decline</button>
            <button style={s.btnGray} onClick={() => setDeclineModal(null)}>Cancel</button>
          </div>
        </div>
      )}

      {returnModal && (
        <div style={s.modalBackdrop} onClick={() => setReturnModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Request return</h3>
            {['additional_permission_needed', 'conflict_of_interest', 'misclassified', 'workload_illness', 'other'].map((r) => (
              <label key={r} style={s.radioLabel}>
                <input type="radio" name="return_reason" checked={returnModal.reason === r} onChange={() => setReturnModal((m) => ({ ...m, reason: r }))} /> {r.replace(/_/g, ' ')}
              </label>
            ))}
            <textarea style={s.textarea} placeholder="Note (optional)" value={returnModal.note} onChange={(e) => setReturnModal((m) => ({ ...m, note: e.target.value }))} />
            <button style={s.btnGray} disabled={!returnModal.reason} onClick={submitReturn}>Request return</button>
            <button style={s.btnGray} onClick={() => setReturnModal(null)}>Cancel</button>
          </div>
        </div>
      )}

      {doneModal && (
        <div style={s.modalBackdrop} onClick={() => setDoneModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Submit completed work</h3>
            <p style={s.hint}>A completion report is required. Submitting is not the same as acceptance — the owner reviews it and records a decision, which generates a Delivery Note.</p>
            <textarea style={s.textarea} placeholder="What was done / the outcome" value={doneModal.report} onChange={(e) => setDoneModal((m) => ({ ...m, report: e.target.value }))} />
            <button style={s.btnGreen} disabled={!doneModal.report.trim() || doneModal.busy} onClick={submitDone}>{doneModal.busy ? 'Submitting…' : 'Submit'}</button>
            <button style={s.btnGray} onClick={() => setDoneModal(null)}>Cancel</button>
          </div>
        </div>
      )}

      {extendModal && (
        <div style={s.modalBackdrop} onClick={() => setExtendModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Request more time</h3>
            <p style={s.hint}>Until the owner decides, the current deadline still applies.</p>
            <select style={s.textarea} value={extendModal.minutes} onChange={(e) => setExtendModal((m) => ({ ...m, minutes: Number(e.target.value) }))}>
              <option value={45}>45 minutes</option>
              <option value={120}>2 hours</option>
              <option value={240}>4 hours</option>
              <option value={1440}>1 day</option>
            </select>
            <textarea style={s.textarea} placeholder="Reason (required)" value={extendModal.reason} onChange={(e) => setExtendModal((m) => ({ ...m, reason: e.target.value }))} />
            <button style={s.btnGreen} disabled={!extendModal.reason.trim() || extendModal.busy} onClick={submitExtend}>{extendModal.busy ? 'Sending…' : 'Send request'}</button>
            <button style={s.btnGray} onClick={() => setExtendModal(null)}>Cancel</button>
          </div>
        </div>
      )}

      {commentsPanel && (
        <div style={s.modalBackdrop} onClick={() => setCommentsPanel(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Comments — {commentsPanel.displayId}</h3>
            {commentsPanel.loading ? <p>Loading…</p> : (
              <div style={s.commentList}>
                {commentsPanel.comments.map((c) => (
                  <div key={c.id} style={s.commentRow}>
                    <div style={s.commentMeta}><strong>{c.author?.full_name || c.author?.email || 'Unknown'}</strong> · {new Date(c.created_at).toLocaleString()}</div>
                    <div>{c.content}</div>
                  </div>
                ))}
                {commentsPanel.comments.length === 0 && <p style={s.empty}>No comments yet.</p>}
              </div>
            )}
            <textarea style={s.textarea} placeholder="Add a comment" value={commentsPanel.draft} onChange={(e) => setCommentsPanel((p) => ({ ...p, draft: e.target.value }))} />
            <button style={s.btnGreen} disabled={!commentsPanel.draft.trim()} onClick={submitComment}>Add comment</button>
            <button style={s.btnGray} onClick={() => setCommentsPanel(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  page: { padding: 24, maxWidth: 800 },
  h1: { fontSize: 22, fontWeight: 800, marginBottom: 8 },
  h2: { fontSize: 15, fontWeight: 800, marginTop: 24, marginBottom: 10, color: '#374151' },
  hint: { color: '#6b7280', fontSize: 13, marginBottom: 14 },
  empty: { color: '#9ca3af', fontSize: 13 },
  card: { background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 10 },
  cardTop: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' },
  muted: { color: '#9ca3af', fontSize: 12.5 },
  pinInput: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 16, letterSpacing: '0.3em', width: 140 },
  errorText: { color: '#b91c1c', fontSize: 12.5, marginTop: 6 },
  priorityBadge: { marginLeft: 'auto', fontSize: 10.5, fontWeight: 800, color: '#991b1b', background: '#fee2e2', borderRadius: 999, padding: '2px 8px' },
  reworkNote: { background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', borderRadius: 8, padding: 8, fontSize: 12.5, marginBottom: 8 },
  message: { fontSize: 13, color: '#374151', fontStyle: 'italic', margin: '6px 0' },
  deadlines: { display: 'flex', gap: 14, fontSize: 12, color: '#6b7280', marginBottom: 8, flexWrap: 'wrap' },
  actions: { display: 'flex', gap: 8 },
  btnGreen: { padding: '7px 14px', borderRadius: 6, border: 'none', background: '#16a34a', color: 'white', cursor: 'pointer', fontSize: 12.5, fontWeight: 700 },
  btnRed: { padding: '7px 14px', borderRadius: 6, border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontSize: 12.5, fontWeight: 700 },
  btnGray: { padding: '7px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: 12.5 },
  modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 },
  modal: { background: 'white', borderRadius: 12, padding: 20, width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 8 },
  radioLabel: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 },
  textarea: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, minHeight: 60, resize: 'vertical' },
  commentList: { maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 },
  commentRow: { background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 8, padding: 8, fontSize: 13 },
  commentMeta: { fontSize: 11.5, color: '#9ca3af', marginBottom: 2 },
};

export default MyTasks;
